import React, { useRef, useMemo } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { Card, CardBody, CardFooter, CardHeader } from "reactstrap";
import { constants } from "../../../../config";
import { useSelector } from "react-redux";
import { useMapController } from "./hooks";
import { generateHexagonsForAllPoints } from "./utils/hexagonUtils";
import FiltersBar from "../../../Common/FiltersBar";
import { useFilters } from "../../../../Context/FiltersContext";

mapboxgl.accessToken = constants.REACT_APP_MAPBOX;

const MapMultiple = ({
	title,
	points,
	zoom,
	handleMarkerClick,
	selectedEstablishmentId,
	fullscreen = false,
	showHexagons = true,
	hexagonConfig = {
		count: 8,
		radiusMeters: 10,
		centerCoords: null,
	},
	polygons = [],
	mapboxStyles,
	leftPanel = null,
	rightPanel = null,
	actionButtons = null,
}) => {
	const mapContainer = useRef(null);
	const layoutModeType = useSelector((state) => state.Layout.layoutModeType);

	const memoizedHexagons = useMemo(() => {
		if (points && points.length > 0 && hexagonConfig) {
			return generateHexagonsForAllPoints(points, hexagonConfig);
		}
		return [];
	}, [points, hexagonConfig]);

	const computedMapboxStyles = useMemo(() => {
		if (mapboxStyles) {
			return mapboxStyles;
		}
		
		return layoutModeType === "dark"
			? "mapbox://styles/mapbox/dark-v10"
			: "mapbox://styles/mapbox/light-v11";
	}, [layoutModeType, mapboxStyles]);

	useMapController({
		mapContainer,
		points: points || [],
		zoom: zoom || 10,
		mapboxStyles: computedMapboxStyles,
		selectedEstablishmentId,
		handleMarkerClick,
		polygons: polygons || [],
		showHexagons,
		hexagonConfig,
		memoizedHexagons,
	});

	const FiltersBarComponent = () => {
		const {
			showFilters,
			isLoadingEstablishments,
			filterOptions,
			selectedRegions,
			selectedBarrios,
			selectedCentros,
			selectedEmpresas,
			selectedAreas,
			setSelectedRegions,
			setSelectedBarrios,
			setSelectedCentros,
			setSelectedEmpresas,
			setSelectedAreas,
			filteredEstablishments,
			clearAllFilters
		} = useFilters();

		return (
			<FiltersBar
				showFilters={showFilters}
				filtersEnabled={{
					region: true,
					barrio: true,
					centro: true,
					empresa: false,
					area: true
				}}
				isLoading={isLoadingEstablishments}
				filterOptions={filterOptions}
				selectedRegions={selectedRegions}
				selectedBarrios={selectedBarrios}
				selectedCentros={selectedCentros}
				selectedEmpresas={selectedEmpresas}
				selectedAreas={selectedAreas}
				setSelectedRegions={setSelectedRegions}
				setSelectedBarrios={setSelectedBarrios}
				setSelectedCentros={setSelectedCentros}
				setSelectedEmpresas={setSelectedEmpresas}
				setSelectedAreas={setSelectedAreas}
				filteredCount={filteredEstablishments?.length || 0}
				onClearFilters={clearAllFilters}
			/>
		);
	};

	if (fullscreen) {
		return (
			<div className="fullscreen-map-container">
				<FiltersBarComponent />
				<div className="fullscreen-map-content">
					{actionButtons}
					<div className="widgets-overlay-container">
						{leftPanel}
						{rightPanel}
					</div>
					<div
						ref={mapContainer}
						style={{ width: "100%", height: "100%" }}
					></div>
				</div>
			</div>
		);
	}

	return (
		<div style={{ position: 'relative', height: 'calc(100vh - 168px)' }}>

			<Card className="p-0 mb-10 card-height-100 map-size">
				{title && (
					<CardHeader className="p-2">
						<h5 className="card-title mb-0">
							<i className="ri-pushpin-line align-middle me-1 text-muted"></i>
							{title}
						</h5>
					</CardHeader>
				)}
				<CardBody className="m-0 p-0">
					{actionButtons}
					<div className="widgets-overlay-container">
						{leftPanel}
						{rightPanel}
					</div>
					<div
						ref={mapContainer}
						style={{ width: "100%", minHeight: "100%" }}
					></div>
				</CardBody>
			</Card>
		</div>
	);
};

export default MapMultiple;
